/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable, Optional} from '@angular/core';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import * as _ from 'lodash';
import {forkJoin, from, map, Observable, of, switchMap, throwError} from 'rxjs';

import {AssetService} from '../creative/asset/asset.service';
import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {SignalAdminResourceApiBase, SignalAdminResourceModel} from '../common/controller/signal-admin-resource-api';
import type {CreativeCopyOptions} from '../creative/creative-api.service';

export interface LibraryCreativeModel extends SignalAdminResourceModel {
  [key: string]: any;
}

@Injectable({providedIn: 'root'})
export class LibraryCreativeApiService extends SignalAdminResourceApiBase<LibraryCreativeModel> {
  constructor(client: AdnApiClientService, private readonly assets: AssetService, @Optional() localUserProfile?: LocalUserProfile) {
    super(client, 'librarycreatives', {}, () => localUserProfile?.isReadOnly() || false);
  }

  getHydrated(id: string): Observable<LibraryCreativeModel> {
    return this.get(id).pipe(switchMap((creative) => {
      if (!creative) {
        return of(creative);
      }
      const libraryCreativeId = _.get(creative, ['libraryCreative', 'id']);
      const ownAssets = from(this.assets.fromCreative(creative.id, undefined));
      const libraryAssets = libraryCreativeId
        ? from(this.assets.fromCreative(libraryCreativeId, undefined))
        : of(undefined);
      return forkJoin({ownAssets, libraryAssets}).pipe(map(({ownAssets: assets, libraryAssets: linkedAssets}) => ({
        ...creative,
        assets,
        ...(libraryCreativeId ? {libraryCreativeAssets: linkedAssets} : {})
      })));
    }));
  }

  copyCreative(source: LibraryCreativeModel | string, options: CreativeCopyOptions): Observable<void> {
    const id = typeof source === 'string' ? source : source?.id;
    if (!id) {
      return throwError(() => 'Insufficient parameters for the creative copy');
    }
    const body = {
      id,
      creativeSetId: options.creativeSetId || '',
      lineItemId: options.lineItemId || '',
      creativeId: options.creativeId || '',
      name: options.name || (typeof source === 'string' ? '' : source.name),
      userState: options.userState || '',
      objectState: options.objectState || null,
      transcodeConfig: options.transcodeConfig ? JSON.stringify(options.transcodeConfig) : '',
      dimensions: options.dimensions || null
    };
    return this.client.request<void>({
      method: 'POST',
      url: this.resourceUri(['librarycreatives', id, 'copy']),
      params: _.pick(body, ['lineItemId', 'creativeId', 'name', 'userState']),
      body
    });
  }
}
